// Reusable infinite-scroll-style hook for any cortex API endpoint that
// returns `IPaginated<T>` (`{ items, total, limit, offset, hasMore }`).
// The hook wraps `useInfiniteQuery` so callers get accumulated items +
// `loadMore` / `hasMore` / `total` without re-implementing the same
// pagination glue per list page. (DEV-16158.)
//
// Two surfaces consumers care about:
// - `items`: flattened across loaded pages — drop straight into a map
// - `loadMore()`: button onClick handler; no-op when already loading or
//   when the server reports `hasMore: false`
//
// For classic numbered-page UIs we'd want a separate `usePaginatedPage`
// hook that drives `offset` from page state directly; ship that when a
// surface actually needs it. Infinite scroll is the common case for
// cortex (workflows / runs / agents browse).
import type { IPaginated } from '@cortex/types';
import { useInfiniteQuery } from '@tanstack/react-query';

/** Defaults align with the API's `defaultLimit` (50). */
const defaultPageSize = 50;

export interface IPaginatedListResult<T> {
  /** All items loaded so far, flattened in fetch order. */
  items: T[];
  /** Server-reported total — useful for "showing X of Y" displays. */
  total: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasMore: boolean;
  error: Error | null;
  /** Append the next page. Safe to call when already loading. */
  loadMore: () => void;
  refetch: () => void;
}

export function usePaginatedList<T>({
  queryKey,
  fetchPage,
  pageSize = defaultPageSize,
  enabled = true,
}: {
  /** Stable cache key — same conventions as react-query. */
  queryKey: readonly unknown[];
  /** Caller fetches one page; the hook handles offset bookkeeping. */
  fetchPage: (params: { limit: number; offset: number }) => Promise<IPaginated<T>>;
  pageSize?: number;
  enabled?: boolean;
}): IPaginatedListResult<T> {
  const query = useInfiniteQuery({
    enabled,
    queryKey,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchPage({ limit: pageSize, offset: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.offset + lastPage.items.length : undefined,
  });

  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const total = pages[pages.length - 1]?.total ?? 0;
  const hasMore = query.hasNextPage;

  return {
    items,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasMore,
    error: query.error as Error | null,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        query.fetchNextPage().catch((err) => {
          console.error('usePaginatedList:loadMore', err);
        });
      }
    },
    refetch: () => {
      query.refetch().catch((err) => {
        console.error('usePaginatedList:refetch', err);
      });
    },
  };
}
